import * as Cookies from "./cookies.ts";

type FeatureFlagPasses = Partial<Record<string, boolean>>;

export function parsePasses(cookie: string): FeatureFlagPasses {
    let flagsOnString = "";
    let flagsOffString = "";
    if (cookie.indexOf("|") !== -1) {
        [flagsOnString, flagsOffString] = cookie.split("|");
    } else {
        flagsOnString = cookie;
    }

    const flagsOn = flagsOnString.split("\n").filter((f) => f !== "");
    const flagsOff = flagsOffString.split("\n").filter((f) => f !== "");

    const result: Record<string, boolean> = {};
    flagsOn.forEach((f) => (result[f] = true));
    flagsOff.forEach((f) => (result[f] = false));
    return result;
}

export function formatCookie(passes: FeatureFlagPasses): string {
    const flagsOn = Object.keys(passes).filter((f) => passes[f] === true);
    const flagsOff = Object.keys(passes).filter((f) => passes[f] === false);

    return `${flagsOn.join("\n")}|${flagsOff.join("\n")}`;
}

/**
 * This function retrieves the status of a specific feature flag from the
 * cookies.
 *
 * @param {string} name - The name of the feature flag to retrieve the status
 * for.
 * @returns {boolean | null | undefined} - The status of the feature flag.
 * Returns true if the flag is on, false if it's off, null if the flag is not
 * set, and undefined if the cookies do not contain any information about the
 * flag.
 */
export function getPass(name: string): boolean | null | undefined {
    const cookie = Cookies.readCookie("flags") || "";
    return parsePasses(decodeURIComponent(cookie))[name];
}

/**
 * This function sets the status of a specific feature flag in the cookies.
 *
 * @param {string} name - The name of the feature flag to set the status for.
 * @param {boolean | null} pass - The status to set for the feature flag. If
 * this is true, the flag will be set to on. If this is false, the flag will be
 * set to off. If this is null, the flag will be removed from the cookies.
 */
export function setPass(name: string, pass?: boolean | null) {
    const cookie = Cookies.readCookie("flags") || "";
    const passes = parsePasses(decodeURIComponent(cookie));
    if (pass == null) {
        delete passes[name];
    } else {
        passes[name] = pass;
    }
    const newCookie = formatCookie(passes);
    Cookies.createCookie(
        "flags",
        Cookies.CookieCategory.Functional,
        encodeURIComponent(newCookie),
    );
}
